import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { APP_NAME, ROUTES } from '../../constants';
import { useNavigationGuard } from '../../hooks/useNavigationGuard';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { hasAdminAccess, hasSuperAdminAccess } from '../../utils/auth.utils';

const NAV_ITEMS = [
  {
    id: 'create',
    label: 'Create CR',
    route: ROUTES.CREATE_CR,
    icon: 'create',
    description: 'Create new construction review',
    superadminOnly: false,
  },
  {
    id: 'review',
    label: 'Review CRs',
    route: ROUTES.REVIEW_CR,
    icon: 'review',
    description: 'View and edit existing CRs',
    superadminOnly: false,
  },
  {
    id: 'admin',
    label: 'Settings',
    route: ROUTES.SETTINGS,
    icon: 'admin',
    description: 'Manage system settings',
    superadminOnly: false,
  },
];

const NavIcon: React.FC<{ type: string; className?: string }> = ({ type, className = "" }) => {
  const icons = {
    gear: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />,
    create: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
    review: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />,
    admin: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />,
    users: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />,
    plus: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />,
    document: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
    settings: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />,
    sun: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v2m0 14v2m9-9h-2M5 12H3m14.364-6.364l-1.414 1.414M7.05 16.95l-1.414 1.414M16.95 16.95l1.414 1.414M7.05 7.05 5.636 5.636M12 8a4 4 0 100 8 4 4 0 000-8z" />,
    menu: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />,
    close: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />,
  };
  
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {icons[type as keyof typeof icons] || icons.gear}
    </svg>
  );
};

export const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { confirmNavigation } = useNavigationGuard();
  const authUser = useAppSelector(state => state.auth.user);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  
  const isHomePage = location.pathname === ROUTES.HOME;
  const isSuperAdmin = hasSuperAdminAccess(authUser?.role);
  const isAdminOrAbove = hasAdminAccess(authUser?.role);
  const visibleNavItems = NAV_ITEMS.filter(item => !item.superadminOnly || isSuperAdmin);

  // Close user menu on click outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const isActive = (path: string) => {
    if (path === ROUTES.CREATE_CR) {
      return location.pathname.startsWith('/create');
    }
    if (path === ROUTES.REVIEW_CR) {
      return location.pathname.startsWith('/review');
    }
    if (path === ROUTES.SETTINGS) {
      return location.pathname.startsWith('/settings');
    }
    if (path === ROUTES.USERS) {
      return location.pathname.startsWith('/users');
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetPath: string) => {
    e.preventDefault();
    
    if (confirmNavigation(targetPath)) {
      navigate(targetPath);
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav className="nav-glass sticky top-0 z-50 w-full relative bg-slate-900/90 border-b border-white/10 backdrop-blur-xl">
      <div className="mx-[4%]">
        <div className="flex items-center justify-between h-16 3xl:h-[72px]">
          {/* Brand */}
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 flex items-center justify-center transition-transform duration-200 hover:scale-105">
              <NavIcon type="sun" className="w-8 h-8 3xl:w-9 3xl:h-9 text-amber-300" />
            </div>
            <Link to={ROUTES.HOME} className="hover:opacity-90 transition-opacity duration-200">
              <h1 className="text-xl 3xl:text-2xl text-white tracking-[0.1em]">
                {APP_NAME}
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation + User profile */}
          <div className={`hidden md:flex items-center gap-6 3xl:gap-8 h-full ${isHomePage ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            {visibleNavItems.map((item) => (
              <Link
                key={item.id}
                to={item.route}
                onClick={(e) => handleNavClick(e, item.route)}
                className={`
                  group relative text-sm 3xl:text-base font-semibold transition-colors duration-200 flex items-center gap-1.5 py-2
                  ${isActive(item.route)
                    ? 'text-amber-200'
                    : 'text-slate-200 hover:text-white'
                  }
                `}
              >
                <span className={`flex h-7 w-7 3xl:h-8 3xl:w-8 items-center justify-center rounded-full ${
                  isActive(item.route)
                    ? 'text-amber-200'
                    : 'text-slate-200'
                }`}>
                  <NavIcon type={item.icon} className="w-4 h-4 3xl:w-5 3xl:h-5" />
                </span>
                <span>{item.label}</span>
              </Link>
            ))}

            {/* Divider */}
            {authUser && <div className="w-px h-6 bg-white/15" />}

            {/* User profile dropdown */}
            {authUser && (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 2xl:gap-2.5 rounded-full px-2 2xl:px-3 py-1.5 2xl:py-2 hover:bg-white/10 transition-colors"
              >
                {authUser.avatar_url ? (
                  <img src={authUser.avatar_url} alt={authUser.name} className="w-7 h-7 2xl:w-8 2xl:h-8 3xl:w-9 3xl:h-9 rounded-full border border-white/20" />
                ) : (
                  <div className="w-7 h-7 2xl:w-8 2xl:h-8 3xl:w-9 3xl:h-9 rounded-full bg-white/10 flex items-center justify-center text-xs 2xl:text-sm font-bold text-white">
                    {authUser.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <span className="text-xs 2xl:text-sm 3xl:text-base text-slate-300 font-medium hidden lg:block">{authUser.name}</span>
                <svg className={`w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-slate-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-1.5 w-36 2xl:w-44 3xl:w-48 rounded-md bg-white border border-slate-200 shadow-md py-0.5 2xl:py-1 z-50">
                  {isAdminOrAbove && (
                    <button
                      onClick={() => { setIsUserMenuOpen(false); navigate(ROUTES.ANALYTICS); }}
                      className="w-full flex items-center gap-2 2xl:gap-2.5 px-3 2xl:px-4 py-1.5 2xl:py-2 3xl:py-2.5 text-xs 2xl:text-sm 3xl:text-base text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3v18h18M7 15l4-4 4 4 5-6" />
                      </svg>
                      Analytics
                    </button>
                  )}
                  {isAdminOrAbove && (
                    <button
                      onClick={() => { setIsUserMenuOpen(false); navigate(ROUTES.CLIENTS); }}
                      className="w-full flex items-center gap-2 2xl:gap-2.5 px-3 2xl:px-4 py-1.5 2xl:py-2 3xl:py-2.5 text-xs 2xl:text-sm 3xl:text-base text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Clients
                    </button>
                  )}
                  {isSuperAdmin && (
                    <button
                      onClick={() => { setIsUserMenuOpen(false); navigate(ROUTES.USERS); }}
                      className="w-full flex items-center gap-2 2xl:gap-2.5 px-3 2xl:px-4 py-1.5 2xl:py-2 3xl:py-2.5 text-xs 2xl:text-sm 3xl:text-base text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      Users
                    </button>
                  )}
                  <button
                    onClick={() => { setIsUserMenuOpen(false); dispatch(logout()); navigate('/login'); }}
                    className="w-full flex items-center gap-2 2xl:gap-2.5 px-3 2xl:px-4 py-1.5 2xl:py-2 3xl:py-2.5 text-xs 2xl:text-sm 3xl:text-base text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Mobile menu button - Always reserve exact space */}
          <div className="md:hidden w-[48px] flex justify-end">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-full bg-white/10 text-white shadow-md transition-all duration-200 hover:bg-white/20 ${isHomePage ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              <NavIcon type={isMobileMenuOpen ? 'close' : 'menu'} className="w-5 h-5" />
            </button>
          </div>
        </div>


        {/* Mobile Navigation Menu - Hidden on home page */}
        {!isHomePage && isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-slate-900/95 backdrop-blur">
            <div className="py-3 space-y-2 px-3">
              {visibleNavItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.route}
                  onClick={(e) => handleNavClick(e, item.route)}
                  className={`
                    block px-4 py-3 text-sm font-semibold transition-all duration-200 relative rounded-2xl
                    ${isActive(item.route)
                      ? 'text-white bg-white/10 shadow-md shadow-black/20'
                      : 'text-slate-200 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full ${
                      isActive(item.route) ? 'text-amber-200' : 'text-slate-200'
                    }`}>
                      <NavIcon type={item.icon} className="w-5 h-5" />
                    </span>
                    <div>
                      <div className="font-medium">{item.label}</div>
                      <div className="text-xs text-slate-400 font-normal mt-0.5">{item.description}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
